import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import PubNub from 'pubnub';
import moment from 'moment';
import {
  FiActivity, FiArrowLeft, FiPlay, FiSquare,
  FiCalendar, FiBell, FiAlertCircle, FiX,
  FiTrendingUp,
} from 'react-icons/fi';
import { attendanceApi, closuresApi, messagesApi } from '../../services/attendance';
import config from '../../config';

// ─── Constants ────────────────────────────────────────────────────────────────
const BREAK_REASONS = ['Lunch', 'Personal', 'Technical issue', 'Meeting', 'Prayer', 'Other'];

function elapsedMs(since) {
  return since ? Math.max(0, Date.now() - new Date(since).getTime()) : 0;
}

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── Animations ───────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const slideIn = keyframes`
  from { transform: translateX(120%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// ─── Shared Styled Components ─────────────────────────────────────────────────
const Page = styled.div`
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #020617;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(2, 6, 23, 0.85);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.6rem;
  height: 3.5rem;
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.3rem;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  border-radius: 0.6rem;
  transition: background 0.15s, color 0.15s;
  &:hover { background: #1e293b; color: #fff; }
`;

const NavTitle = styled.div`
  font-weight: 700;
  font-size: 1.4rem;
  color: #ffffff;
`;

const NavSub = styled.span`
  font-size: 1.2rem;
  color: #94a3b8;
  margin-left: 0.6rem;
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${p => p.color || '#94a3b8'};
`;

const StatusDot = styled.span`
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 50%;
  background: ${p => p.color || '#64748b'};
  display: inline-block;
  flex-shrink: 0;
  animation: ${p => p.pulse ? pulse : 'none'} 2s ease-in-out infinite;
`;

const Content = styled.div`
  max-width: 90rem;
  margin: 0 auto;
  padding: 2rem 1.6rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.6rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 1.2rem;
  overflow: hidden;
`;

const CardPad = styled(Card)`
  padding: 1.6rem;
`;

const StatusCard = styled(Card)`
  padding: 1.6rem;
  border-color: ${p =>
    p.state === 'online' ? 'rgba(52,211,153,0.25)' :
    p.state === 'break'  ? 'rgba(251,191,36,0.25)' :
    '#1e293b'};
  background: ${p =>
    p.state === 'online' ? 'linear-gradient(135deg, rgba(5,150,105,0.08) 0%, #0f172a 50%)' :
    p.state === 'break'  ? 'linear-gradient(135deg, rgba(120,53,15,0.15) 0%, #0f172a 50%)' :
    '#0f172a'};
`;

const BigStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  margin-bottom: 1.6rem;
`;

const StatusIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p =>
    p.state === 'online' ? 'rgba(52,211,153,0.15)' :
    p.state === 'break'  ? 'rgba(251,191,36,0.15)' :
    '#1e293b'};
  color: ${p =>
    p.state === 'online' ? '#34d399' :
    p.state === 'break'  ? '#fbbf24' :
    '#64748b'};
  font-size: 2rem;
  flex-shrink: 0;
`;

const BigStatusText = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
`;

const BigStatusSub = styled.div`
  font-size: 1.2rem;
  color: ${p => p.color || '#94a3b8'};
  margin-top: 0.2rem;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.4rem;
  border-radius: 0.8rem;
  border: none;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:active:not(:disabled) { transform: scale(0.97); }
`;

const BtnSuccess = styled(Btn)`
  background: rgba(52, 211, 153, 0.15);
  color: #34d399;
  border: 1px solid rgba(52, 211, 153, 0.3);
  &:hover:not(:disabled) { background: rgba(52, 211, 153, 0.25); }
`;

const BtnWarning = styled(Btn)`
  background: rgba(251, 191, 36, 0.12);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.3);
  &:hover:not(:disabled) { background: rgba(251, 191, 36, 0.22); }
`;

const BtnDanger = styled(Btn)`
  background: rgba(248, 113, 113, 0.1);
  color: #f87171;
  border: 1px solid rgba(248, 113, 113, 0.3);
  &:hover:not(:disabled) { background: rgba(248, 113, 113, 0.2); }
`;

const BtnGhost = styled(Btn)`
  background: #1e293b;
  color: #94a3b8;
  border: 1px solid #334155;
  &:hover { color: #fff; background: #334155; }
`;

const SummaryPills = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.2rem;
  padding-top: 1.2rem;
  border-top: 1px solid rgba(255,255,255,0.05);
`;

const Pill = styled.div`
  text-align: center;
`;

const PillVal = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${p => p.color || '#fff'};
`;

const PillLabel = styled.div`
  font-size: 1rem;
  color: #64748b;
  margin-top: 0.2rem;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #1e293b;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 1.6rem;
  border: none;
  background: none;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${p => p.active ? '#ffffff' : '#64748b'};
  border-bottom: 2px solid ${p => p.active ? '#a78bfa' : 'transparent'};
  cursor: pointer;
  transition: color 0.15s;
  &:hover { color: #cbd5e1; }
`;

const TabContent = styled.div`
  padding: 1.4rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.2rem;
`;

const Th = styled.th`
  text-align: ${p => p.right ? 'right' : 'left'};
  padding: 0.8rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #1e293b;
`;

const Td = styled.td`
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #1e293b;
  text-align: ${p => p.right ? 'right' : 'left'};
  color: ${p => p.color || '#cbd5e1'};
  font-variant-numeric: tabular-nums;
  &:last-child { border-bottom: none; }
`;

const Mono = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
`;

// Modals
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1.6rem;
`;

const ModalBox = styled.div`
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 1.2rem;
  padding: 2rem;
  width: 100%;
  max-width: 36rem;
`;

const ModalTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
`;

const ReasonBtn = styled.button`
  padding: 0.8rem;
  border-radius: 0.8rem;
  border: 1px solid ${p => p.selected ? '#fbbf24' : '#334155'};
  background: ${p => p.selected ? 'rgba(251,191,36,0.12)' : '#1e293b'};
  color: ${p => p.selected ? '#fbbf24' : '#94a3b8'};
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #64748b; color: #fff; }
`;

const TextInput = styled.input`
  width: 100%;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 0.8rem;
  padding: 0.8rem 1rem;
  font-size: 1.3rem;
  color: #fff;
  margin-bottom: 1.2rem;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #fbbf24; }
`;

const BtnPrimary = styled(Btn)`
  background: #7c3aed;
  color: #fff;
  border: none;
  &:hover:not(:disabled) { background: #6d28d9; }
`;

const ModalRow = styled.div`
  display: flex;
  gap: 1rem;
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function LiveTimer({ since, color }) {
  const [elapsed, setElapsed] = useState(() => elapsedMs(since));
  useEffect(() => {
    if (!since) return;
    const iv = setInterval(() => setElapsed(elapsedMs(since)), 1000);
    return () => clearInterval(iv);
  }, [since]);
  return <strong style={{ color }}>{fmtDuration(elapsed)}</strong>;
}

function BreakModal({ onConfirm, onCancel, loading }) {
  const [reason, setReason] = useState('');
  const [custom, setCustom] = useState('');
  return (
    <Overlay>
      <ModalBox>
        <ModalTitle>☕ Start Break</ModalTitle>
        <p style={{ color: '#94a3b8', fontSize: '1.2rem', marginBottom: '1rem' }}>Select a reason:</p>
        <ReasonGrid>
          {BREAK_REASONS.map(r => (
            <ReasonBtn key={r} selected={reason === r} onClick={() => { setReason(r); if (r !== 'Other') setCustom(''); }}>
              {r}
            </ReasonBtn>
          ))}
        </ReasonGrid>
        {reason === 'Other' && (
          <TextInput
            autoFocus
            placeholder="Describe reason…"
            value={custom}
            onChange={e => setCustom(e.target.value)}
          />
        )}
        <ModalRow>
          <BtnGhost style={{ flex: 1 }} onClick={onCancel}>Cancel</BtnGhost>
          <BtnWarning
            style={{ flex: 1 }}
            onClick={() => onConfirm(reason === 'Other' ? custom : reason)}
            disabled={!reason || loading || (reason === 'Other' && !custom.trim())}
          >
            {loading ? 'Starting…' : 'Start Break'}
          </BtnWarning>
        </ModalRow>
      </ModalBox>
    </Overlay>
  );
}

function CheckoutModal({ summary, onClose }) {
  return (
    <Overlay>
      <ModalBox style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>✅</div>
        <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fff', marginBottom: '0.4rem' }}>Session Complete</div>
        <div style={{ color: '#64748b', fontSize: '1.2rem', marginBottom: '1.6rem' }}>Great work today!</div>
        {summary && (
          <div style={{ textAlign: 'left', marginBottom: '1.6rem' }}>
            {[
              { label: 'Total Login Time', value: summary.totalLoginTime, color: '#34d399' },
              { label: 'Total Break Time', value: summary.totalBreakTime, color: '#fbbf24' },
              { label: 'Net Working Time', value: summary.netWorkingTime, color: '#a78bfa' },
              { label: 'Total Sessions',   value: summary.totalSessions,  color: '#60a5fa' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 0', borderBottom: '1px solid #1e293b' }}>
                <span style={{ color: '#94a3b8', fontSize: '1.2rem' }}>{label}</span>
                <span style={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              </div>
            ))}
          </div>
        )}
        <BtnPrimary style={{ width: '100%', justifyContent: 'center' }} onClick={onClose}>Done</BtnPrimary>
      </ModalBox>
    </Overlay>
  );
}

const ToastContainer = styled.div`
  position: fixed;
  top: 1.6rem;
  right: 1.6rem;
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 32rem;
  width: 100%;
`;

const ToastCard = styled.div`
  background: ${p => p.urgent ? 'rgba(127,29,29,0.9)' : 'rgba(15,23,42,0.95)'};
  border: 1px solid ${p => p.urgent ? 'rgba(248,113,113,0.35)' : 'rgba(167,139,250,0.3)'};
  border-radius: 1rem;
  padding: 1.2rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  animation: ${slideIn} 0.25s ease;
`;

const SectionTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.urgent ? '#f87171' : '#a78bfa'};
  margin-bottom: 0.4rem;
`;

const MessageList = styled.div`
  max-height: 24rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const MessageItem = styled.div`
  display: flex;
  gap: 0.8rem;
  padding: 0.8rem;
  border-radius: 0.8rem;
  border: 1px solid ${p => p.urgent ? 'rgba(248,113,113,0.2)' : '#1e293b'};
  background: ${p => p.urgent ? 'rgba(127,29,29,0.2)' : 'rgba(30,41,59,0.4)'};
`;

const TimelineTrack = styled.div`
  position: relative;
  height: 2.5rem;
  background: #1e293b;
  border-radius: 0.6rem;
  overflow: hidden;
`;

const TimelineBar = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  background: ${p => p.color};
  opacity: ${p => p.opacity || 0.5};
`;

function SessionTimeline({ sessions, breaks, currentBreak }) {
  const now = moment();
  const dayStart = moment().startOf('day');
  const totalMs = now.diff(dayStart);

  const toLeft = (dt) => {
    if (!dt) return 0;
    const ms = moment(dt).diff(dayStart);
    return `${Math.min(100, Math.max(0, (ms / totalMs) * 100))}%`;
  };
  const toWidth = (start, end) => {
    const s = moment(start);
    const e = end ? moment(end) : now;
    const ms = Math.max(1, e.diff(s));
    return `${Math.min(100, Math.max(0.5, (ms / totalMs) * 100))}%`;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
        <span style={{ fontSize: '1rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today's Timeline</span>
        <span style={{ fontSize: '1rem', color: '#475569' }}>{moment().format('D MMM YYYY')}</span>
      </div>
      <TimelineTrack>
        {(sessions || []).map((s, i) => (
          <TimelineBar
            key={i}
            color="rgba(52,211,153,0.5)"
            style={{ left: toLeft(s.loginAt || s.login_at), width: toWidth(s.loginAt || s.login_at, s.logoutAt || s.logout_at) }}
          />
        ))}
        {(breaks || []).map((b, i) => (
          <TimelineBar
            key={i}
            color="rgba(251,191,36,0.6)"
            style={{ left: toLeft(b.breakStart || b.break_start), width: toWidth(b.breakStart || b.break_start, b.breakEnd || b.break_end) }}
          />
        ))}
        {currentBreak && (
          <TimelineBar
            color="rgba(251,191,36,0.6)"
            style={{ left: toLeft(currentBreak.breakStartRaw), width: toWidth(currentBreak.breakStartRaw, null) }}
          />
        )}
      </TimelineTrack>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem', fontSize: '1rem', color: '#475569' }}>
        <span>00:00</span>
        <span style={{ color: '#94a3b8' }}>{moment().format('hh:mm A')}</span>
        <span>23:59</span>
      </div>
    </div>
  );
}

// ─── Main DoctorView ──────────────────────────────────────────────────────────
export default function DoctorView({ doctorId, doctorFirstName, insurer, qc, history }) {
  const [status, setStatus]           = useState(null);
  const [closureData, setClosureData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [activeTab, setActiveTab]     = useState('today');
  const [toasts, setToasts]           = useState([]);
  const [msgInbox, setMsgInbox]       = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBreakModal, setShowBreakModal]       = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutSummary, setCheckoutSummary]     = useState(null);
  const [actionLoading, setActionLoading]         = useState(false);
  const pubnubRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try { setStatus(await attendanceApi.status()); } catch (e) { console.error(e); }
  }, []);

  const fetchClosures = useCallback(async () => {
    try { setClosureData({ ...await closuresApi.my(), lastUpdated: Date.now() }); } catch (e) { console.error(e); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const h = await attendanceApi.history(7);
      setHistoryData(h.history);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchClosures();
    fetchHistory();
    const closureIv = setInterval(fetchClosures, 60000);
    return () => clearInterval(closureIv);
  }, []);

  // PubNub for broadcast messages
  useEffect(() => {
    if (!config.pubnubSubscribeKey) return;
    const pn = new PubNub({
      subscribeKey: config.pubnubSubscribeKey,
      publishKey:   'demo',
      uuid:         `dr-attendance-${doctorId}-${Date.now()}`,
    });
    pn.addListener({
      message: ({ message: msg }) => {
        if (!msg) return;
        if (msg.targetInsurer && msg.targetInsurer !== insurer) return;
        const id = msg.id || Date.now();
        setMsgInbox(prev => [{ ...msg, id, receivedAt: Date.now() }, ...prev]);
        setUnreadCount(c => c + 1);
        setToasts(t => [...t, { ...msg, id }]);
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 12000);
      },
    });
    pn.subscribe({ channels: ['dr-dashboard-broadcast'] });
    pubnubRef.current = pn;

    // Load active messages into inbox
    messagesApi.active().then(({ messages: msgs = [] }) => {
      if (msgs.length) setMsgInbox(msgs.map(m => ({ ...m, receivedAt: null })));
    }).catch(() => {});

    return () => { pn.unsubscribeAll(); };
  }, [doctorId, insurer]);

  const handleCheckIn = async () => {
    setActionLoading(true);
    try { await attendanceApi.checkIn(); await fetchStatus(); } catch (e) { alert((e.response && e.response.data && e.response.data.message) || e.message); }
    setActionLoading(false);
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const res = await attendanceApi.checkOut();
      setCheckoutSummary(res.summary);
      setShowCheckoutModal(true);
      await fetchStatus();
    } catch (e) { alert((e.response && e.response.data && e.response.data.message) || e.message); }
    setActionLoading(false);
  };

  const handleStartBreak = async (reason) => {
    setActionLoading(true);
    try { await attendanceApi.startBreak(reason); await fetchStatus(); setShowBreakModal(false); } catch (e) { alert((e.response && e.response.data && e.response.data.message) || e.message); }
    setActionLoading(false);
  };

  const handleEndBreak = async () => {
    setActionLoading(true);
    try { await attendanceApi.endBreak(); await fetchStatus(); } catch (e) { alert((e.response && e.response.data && e.response.data.message) || e.message); }
    setActionLoading(false);
  };

  const state = (status && status.currentState) || 'offline';
  const dotColor = state === 'online' ? '#34d399' : state === 'break' ? '#fbbf24' : '#64748b';
  const statusLabel = state === 'online' ? 'Online' : state === 'break' ? 'On Break' : 'Offline';

  return (
    <Page>
      {/* Broadcast Toasts */}
      <ToastContainer>
        {toasts.map(toast => (
          <ToastCard key={toast.id} urgent={toast.priority === 'urgent'}>
            <div style={{ fontSize: '1.6rem', flexShrink: 0 }}>
              {toast.priority === 'urgent' ? '🚨' : '📢'}
            </div>
            <div style={{ flex: 1 }}>
              <SectionTitle urgent={toast.priority === 'urgent'}>
                {toast.priority === 'urgent' ? 'URGENT' : 'Admin Broadcast'}
              </SectionTitle>
              <div style={{ fontSize: '1.2rem', color: '#e2e8f0' }}>{toast.message}</div>
            </div>
            <button
              onClick={() => setToasts(t => t.filter(x => x.id !== toast.id))}
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.4rem' }}
            >
              <FiX />
            </button>
          </ToastCard>
        ))}
      </ToastContainer>

      {/* Modals */}
      {showBreakModal && (
        <BreakModal onConfirm={handleStartBreak} onCancel={() => setShowBreakModal(false)} loading={actionLoading} />
      )}
      {showCheckoutModal && (
        <CheckoutModal summary={checkoutSummary} onClose={() => { setShowCheckoutModal(false); setCheckoutSummary(null); }} />
      )}

      {/* Header */}
      <TopNav>
        <NavLeft>
          <BackBtn onClick={() => history.push('/home')}><FiArrowLeft size={14} /> Back</BackBtn>
          <FiActivity color="#a78bfa" size={16} />
          <NavTitle>{doctorFirstName || 'Doctor'}<NavSub>· {insurer}</NavSub></NavTitle>
        </NavLeft>
        <StatusPill color={dotColor}>
          <StatusDot color={dotColor} pulse={state !== 'offline'} />
          {statusLabel}
        </StatusPill>
      </TopNav>

      <Content>
        <Grid>
          {/* Left: status + timeline/history */}
          <div>
            {/* Status Card */}
            <StatusCard state={state} style={{ marginBottom: '1.4rem' }}>
              <BigStatusRow>
                <StatusIcon state={state}>
                  {state === 'online' && <FiPlay size={22} />}
                  {state === 'break'  && <span style={{ fontSize: '2rem' }}>☕</span>}
                  {state === 'offline' && <FiSquare size={22} />}
                </StatusIcon>
                <div>
                  <BigStatusText>
                    {state === 'online' ? "You're Online" : state === 'break' ? 'On a Break' : 'Offline'}
                  </BigStatusText>
                  {state === 'online' && status && status.currentSession && status.currentSession.loginAtRaw && (
                    <BigStatusSub color="#34d399">
                      Online for{' '}
                      <LiveTimer since={status.currentSession.loginAtRaw} color="#34d399" />
                      <span style={{ color: '#475569', marginLeft: '0.6rem' }}>since {status.currentSession.loginAt}</span>
                    </BigStatusSub>
                  )}
                  {state === 'break' && status && status.currentBreak && status.currentBreak.breakStartRaw && (
                    <BigStatusSub color="#fbbf24">
                      On break for{' '}
                      <LiveTimer since={status.currentBreak.breakStartRaw} color="#fbbf24" />
                      {status.currentBreak.reason && <span style={{ color: '#475569', marginLeft: '0.6rem' }}>· {status.currentBreak.reason}</span>}
                    </BigStatusSub>
                  )}
                  {state === 'offline' && <BigStatusSub>Ready to check in</BigStatusSub>}
                </div>
              </BigStatusRow>

              <ActionRow>
                {state === 'offline' && (
                  <BtnSuccess onClick={handleCheckIn} disabled={actionLoading}>
                    <FiPlay size={14} /> {actionLoading ? 'Checking in…' : 'Check In'}
                  </BtnSuccess>
                )}
                {state === 'online' && (
                  <>
                    <BtnWarning onClick={() => setShowBreakModal(true)} disabled={actionLoading}>
                      ☕ Take a Break
                    </BtnWarning>
                    <BtnDanger onClick={handleCheckOut} disabled={actionLoading}>
                      <FiSquare size={14} /> {actionLoading ? 'Checking out…' : 'Check Out'}
                    </BtnDanger>
                  </>
                )}
                {state === 'break' && (
                  <BtnSuccess onClick={handleEndBreak} disabled={actionLoading}>
                    <FiPlay size={14} /> {actionLoading ? 'Resuming…' : 'Resume Work'}
                  </BtnSuccess>
                )}
              </ActionRow>

              {status && status.todaySummary && (
                <SummaryPills>
                  <Pill>
                    <PillVal color="#34d399">{status.todaySummary.totalLoginTime || '0m'}</PillVal>
                    <PillLabel>Online</PillLabel>
                  </Pill>
                  <Pill>
                    <PillVal color="#fbbf24">{status.todaySummary.totalBreakTime || '0m'}</PillVal>
                    <PillLabel>Breaks</PillLabel>
                  </Pill>
                  <Pill>
                    <PillVal color="#a78bfa">{status.todaySummary.netWorkingTime || '0m'}</PillVal>
                    <PillLabel>Net Work</PillLabel>
                  </Pill>
                </SummaryPills>
              )}
            </StatusCard>

            {/* Timeline / History Tabs */}
            <Card>
              <Tabs>
                <Tab active={activeTab === 'today'} onClick={() => setActiveTab('today')}>
                  <FiActivity size={13} /> Today
                </Tab>
                <Tab active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
                  <FiCalendar size={13} /> 7-Day History
                </Tab>
              </Tabs>
              <TabContent>
                {activeTab === 'today' && (
                  <SessionTimeline
                    sessions={status && status.todaySessions}
                    breaks={status && status.todayBreaks}
                    currentBreak={status && status.currentBreak}
                  />
                )}
                {activeTab === 'history' && (
                  historyData && historyData.length ? (
                    <div style={{ overflowX: 'auto' }}>
                      <Table>
                        <thead>
                          <tr>
                            <Th>Date</Th>
                            <Th right>Login</Th>
                            <Th right>Logout</Th>
                            <Th right>Online</Th>
                            <Th right>Break</Th>
                            <Th right>Net</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyData.map(d => (
                            <tr key={d.date}>
                              <Td>
                                <strong style={{ color: '#fff' }}>{moment(d.date).format('D MMM')}</strong>
                                <span style={{ color: '#475569', marginLeft: '0.4rem', fontSize: '1rem' }}>{moment(d.date).format('ddd')}</span>
                              </Td>
                              <Td right>{d.firstLoginAt || '—'}</Td>
                              <Td right>{d.lastLogoutAt || '—'}</Td>
                              <Td right color="#34d399"><Mono>{d.totalLoginTime}</Mono></Td>
                              <Td right color="#fbbf24"><Mono>{d.totalBreakTime}</Mono></Td>
                              <Td right color="#a78bfa"><Mono><strong>{d.netWorkingTime}</strong></Mono></Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#475569', padding: '3rem 0' }}>No history available.</div>
                  )
                )}
              </TabContent>
            </Card>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
            {/* Closure Card */}
            <CardPad>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <FiTrendingUp color="#a78bfa" size={16} />
                  <strong style={{ color: '#fff', fontSize: '1.3rem' }}>Today's Closures</strong>
                </div>
                {closureData && closureData.lastUpdated && (
                  <span style={{ fontSize: '1rem', color: '#475569' }}>{moment(closureData.lastUpdated).format('hh:mm A')}</span>
                )}
              </div>
              {closureData && closureData.message === 'no_session_today' ? (
                <p style={{ color: '#64748b', fontSize: '1.2rem' }}>Check in to start tracking closures.</p>
              ) : (
                <>
                  <div style={{ fontSize: '4rem', fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums', marginBottom: '0.4rem' }}>
                    {closureData && closureData.total != null ? closureData.total : '—'}
                  </div>
                  <div style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '1rem' }}>cases since first login today</div>
                  {((closureData && closureData.byInsurer) || []).map(({ insurer: ins, count }) => (
                    <div key={ins} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                      <span style={{ fontSize: '1.2rem', color: '#94a3b8', textTransform: 'capitalize' }}>{ins}</span>
                      <span style={{ fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
                    </div>
                  ))}
                </>
              )}
            </CardPad>

            {/* Messages Inbox */}
            <CardPad>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <FiBell color="#a78bfa" size={16} />
                  <strong style={{ color: '#fff', fontSize: '1.3rem' }}>Messages</strong>
                  {unreadCount > 0 && (
                    <span style={{ background: '#ef4444', color: '#fff', fontSize: '1rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: '1rem', animation: `${pulse} 2s infinite` }}>
                      {unreadCount} new
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button onClick={() => setUnreadCount(0)} style={{ fontSize: '1.1rem', color: '#475569', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Mark all read
                  </button>
                )}
              </div>
              {msgInbox.length === 0 ? (
                <p style={{ color: '#475569', fontSize: '1.2rem', textAlign: 'center', padding: '2rem 0' }}>No messages yet.</p>
              ) : (
                <MessageList>
                  {msgInbox.map(msg => (
                    <MessageItem key={msg.id} urgent={msg.priority === 'urgent'}>
                      <div style={{ fontSize: '1.4rem', flexShrink: 0 }}>
                        {msg.priority === 'urgent' ? <FiAlertCircle color="#f87171" size={16} /> : <FiBell color="#a78bfa" size={16} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                          <SectionTitle urgent={msg.priority === 'urgent'} style={{ marginBottom: 0 }}>
                            {msg.priority === 'urgent' ? 'Urgent' : 'Admin'}
                          </SectionTitle>
                          <span style={{ fontSize: '1rem', color: '#475569' }}>
                            {msg.receivedAt ? moment(msg.receivedAt).fromNow() : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: '1.2rem', color: '#cbd5e1' }}>{msg.message}</div>
                      </div>
                    </MessageItem>
                  ))}
                </MessageList>
              )}
            </CardPad>
          </div>
        </Grid>
      </Content>
    </Page>
  );
}
